import { CheckCircle2 } from "lucide-react";
import { useTranslate } from "ra-core";
import { useState } from "react";

import type { Deal } from "../../types";
import { AttentionRow } from "./AttentionRow";
import {
  ATTENTION_GROUPS,
  type AttentionGroupKey,
  type AttentionInbox as Inbox,
  type HomeCounters,
} from "./homeModel";
import { groupAnchor, useMarkDone, useTodayBreakdownLabel } from "./homeUi";
import { NextStepProvider } from "./NextStepSheet";
import { useAttentionLookup } from "./useAttentionLookup";
import { useHomeFormat } from "./useHomeFormat";

/** Rows shown per group before «Ver N más». */
const GROUP_PREVIEW = 4;

const GroupHint = ({
  group,
  inbox,
  counters,
}: {
  group: AttentionGroupKey;
  inbox: Inbox;
  counters: HomeCounters;
}) => {
  const translate = useTranslate();
  const format = useHomeFormat();
  const breakdown = useTodayBreakdownLabel();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(`desk.home.inbox.hints.${key}`, options);
  switch (group) {
    case "overdue":
      return <>{t("oldest", { smart_count: counters.oldestOverdueDays })}</>;
    case "today":
      return <>{breakdown(counters.todayBreakdown)}</>;
    case "no_next_step":
      return (
        <>
          {t("at_stake", { amount: format.money(counters.noNextStepAmount) })}
        </>
      );
    case "collect":
      return (
        <>
          {t("pending", {
            amount: format.money(
              inbox.collect.reduce((sum, item) => sum + (item.amount ?? 0), 0),
            ),
          })}
        </>
      );
  }
};

export interface AttentionGroupsProps {
  inbox: Inbox;
  counters: HomeCounters;
  deals: Deal[];
  variant: "desk" | "mobile";
  /** Rows shown per group before «Ver N más». */
  preview?: number;
}

/**
 * The grouped rows (Vencido / Hoy / Oportunidades sin próximo paso /
 * Cobros) with their inline actions. Each group heading is the anchor the
 * counters jump to.
 */
export const AttentionGroups = ({
  inbox,
  counters,
  deals,
  variant,
  preview = GROUP_PREVIEW,
}: AttentionGroupsProps) => {
  const translate = useTranslate();
  const markDone = useMarkDone();
  const [expanded, setExpanded] = useState<Set<AttentionGroupKey>>(new Set());
  const groups = ATTENTION_GROUPS.filter((group) => inbox[group].length > 0);
  const rowsOf = (group: AttentionGroupKey) =>
    expanded.has(group) ? inbox[group] : inbox[group].slice(0, preview);
  const lookup = useAttentionLookup(groups.flatMap(rowsOf), deals);

  if (groups.length === 0) {
    return (
      <p className="home-empty">
        <CheckCircle2 aria-hidden className="size-5" strokeWidth={1.75} />
        {translate("desk.home.inbox.empty")}
      </p>
    );
  }

  return (
    <NextStepProvider>
      {(onNextStep) =>
        groups.map((group) => {
          const items = inbox[group];
          const isOpen = expanded.has(group);
          return (
            <div
              key={group}
              className="home-group"
              data-group={group}
              data-variant={variant}
            >
              <h3
                id={groupAnchor(group)}
                tabIndex={-1}
                className="home-group__head"
              >
                <span className="home-group__name">
                  <span className="home-group__dot" aria-hidden />
                  {translate(`desk.home.inbox.groups.${group}`)}
                  <span className="home-group__count">
                    {" · "}
                    {items.length}
                  </span>
                </span>
                {variant === "desk" ? (
                  <span className="home-group__hint">
                    <GroupHint
                      group={group}
                      inbox={inbox}
                      counters={counters}
                    />
                  </span>
                ) : null}
              </h3>
              <ul className="home-group__list">
                {rowsOf(group).map((item) => (
                  <AttentionRow
                    key={item.key}
                    item={item}
                    group={group}
                    lookup={lookup}
                    variant={variant}
                    onDone={markDone}
                    onNextStep={onNextStep}
                  />
                ))}
              </ul>
              {items.length > preview ? (
                <button
                  type="button"
                  className="home-group__more"
                  aria-expanded={isOpen}
                  onClick={() =>
                    setExpanded((current) => {
                      const next = new Set(current);
                      if (isOpen) next.delete(group);
                      else next.add(group);
                      return next;
                    })
                  }
                >
                  {isOpen
                    ? translate("desk.home.inbox.show_less")
                    : translate("desk.home.inbox.show_more", {
                        smart_count: items.length - preview,
                      })}
                </button>
              ) : null}
            </div>
          );
        })
      }
    </NextStepProvider>
  );
};

/** Loading placeholder rows. */
export const AttentionLoading = () => (
  <div className="home-inbox__loading" aria-busy="true">
    <span />
    <span />
    <span />
  </div>
);

/**
 * «Necesita tu atención» (desktop card): a single inbox grouped by urgency,
 * with inline actions.
 */
export const AttentionInbox = ({
  isPending,
  ...props
}: Omit<AttentionGroupsProps, "variant"> & { isPending: boolean }) => {
  const translate = useTranslate();
  return (
    <section
      className="home-card home-inbox"
      aria-labelledby="home-inbox-title"
    >
      <header className="home-card__head">
        <h2 id="home-inbox-title" className="home-card__title">
          {translate("desk.home.inbox.title")}
          <span className="home-card__subtitle">
            {translate("desk.home.inbox.subtitle")}
          </span>
        </h2>
      </header>
      {isPending ? (
        <AttentionLoading />
      ) : (
        <AttentionGroups {...props} variant="desk" />
      )}
    </section>
  );
};
